import React from 'react';
import Modal from '../common/Modal.js';
import Button from '../common/Button.js';
import { formatCurrencyShort } from '../../utils/amountHelpers.js';

const formatCurrency = formatCurrencyShort;

const formatNumber = (value) =>
  new Intl.NumberFormat('en-US').format(value || 0);

const formatDateTime = (value) => {
  if (!value) return 'Not sold yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';
  return date.toLocaleString();
};

const getTrendLabel = (trend) => {
  switch (trend) {
    case 'up':
      return 'Trending up';
    case 'down':
      return 'Trending down';
    default:
      return 'Stable';
  }
};

const ProductDetailsDialog = ({ product, isOpen, onClose }) => {
  if (!product) return null;

  const inventoryConsumed = product.inventoryConsumed || 0;
  const trendLabel = getTrendLabel(product.trend);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Product details - ${product.name}`} size="large">
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-3xl bg-slate-50 p-5">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Overview</h3>
            <p className="mt-2 text-sm text-gray-600">Key performance details for this product.</p>
          </div>
          <div className="grid gap-3">
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs uppercase text-gray-500">Branch</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{product.branchName || 'All branches'}</p>
            </div>
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs uppercase text-gray-500">Category</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{product.category || 'Uncategorized'}</p>
            </div>
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs uppercase text-gray-500">Unit price</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{formatCurrency(product.price)}</p>
            </div>
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs uppercase text-gray-500">Trending status</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{trendLabel}</p>
            </div>
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs uppercase text-gray-500">Last sold</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{formatDateTime(product.lastSoldAt)}</p>
            </div>
          </div>
        </div>

        <div className="space-y-4 rounded-3xl bg-slate-50 p-5">
          <div>
            <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Sales & inventory</h3>
          </div>
          <div className="grid gap-3">
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs uppercase text-gray-500">Total orders</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{formatNumber(product.orderCount)}</p>
            </div>
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs uppercase text-gray-500">Quantity sold</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{formatNumber(product.totalOrders)}</p>
            </div>
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs uppercase text-gray-500">Revenue</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{formatCurrency(product.totalRevenue)}</p>
            </div>
            <div className="rounded-2xl bg-white p-4 shadow-sm">
              <p className="text-xs uppercase text-gray-500">Inventory consumed</p>
              <p className="mt-1 text-sm font-medium text-slate-900">{formatNumber(inventoryConsumed)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-6 rounded-3xl bg-slate-50 p-5">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-slate-900">Product information</h3>
            <p className="mt-1 text-sm text-gray-600">Details and recipe for the selected product.</p>
          </div>
        </div>

        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-xs uppercase text-gray-500">Product ID</p>
            <p className="mt-1 text-sm font-medium text-slate-900">{product.id}</p>
          </div>
          <div className="rounded-2xl bg-white p-4 shadow-sm">
            <p className="text-xs uppercase text-gray-500">Description</p>
            <p className="mt-1 text-sm text-gray-600">{product.description || 'No description provided.'}</p>
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-sm font-semibold text-slate-900">Recipe ingredients</h4>
          <p className="mt-1 text-sm text-gray-500">Estimated ingredient list used by this product.</p>
          <div className="mt-4 grid gap-3">
            {Array.isArray(product.recipe) && product.recipe.length > 0 ? (
              product.recipe.map((recipeItem, index) => (
                <div key={index} className="rounded-2xl bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{recipeItem.inventoryName || `Item ${recipeItem.inventoryId || index + 1}`}</p>
                      <p className="text-xs text-gray-500">Inventory ID: {recipeItem.inventoryId || 'N/A'}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-slate-900">{formatNumber(recipeItem.quantity)}</p>
                      <p className="text-xs text-gray-500">per unit</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="rounded-2xl bg-white p-4 text-sm text-gray-500">Recipe information is not available.</div>
            )}
          </div>
        </div>
      </div>

      <Modal.Actions>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ProductDetailsDialog;
